import { useState, useRef, useEffect, useId } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/utils'

// Parse a date value (ISO string or date-only "YYYY-MM-DD") as LOCAL date
// new Date("2026-04-09") → UTC midnight → wrong local day in US timezones
function parseLocalDate(value) {
  if (!value) return null
  const part = typeof value === 'string' ? value.split('T')[0] : value
  const [y, m, d] = String(part).split('-').map(Number)
  return new Date(y, m - 1, d)
}

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']

const CALENDAR_W = 256
const CALENDAR_H = 300

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay()
}

// children prop = custom trigger; if omitted, renders default Calendar button
export function DatePicker({ value, onChange, placeholder = 'Set date', children }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0, bottom: 'auto', flipUp: false })
  const triggerRef = useRef()
  const calendarRef = useRef()
  const uid = useId()
  const today = new Date()
  const init = parseLocalDate(value) || today
  const [viewYear, setViewYear] = useState(init.getFullYear())
  const [viewMonth, setViewMonth] = useState(init.getMonth())

  useEffect(() => {
    if (value) {
      const d = parseLocalDate(value)
      if (d) { setViewYear(d.getFullYear()); setViewMonth(d.getMonth()) }
    }
  }, [value])

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (!triggerRef.current?.contains(e.target) && !calendarRef.current?.contains(e.target)) setOpen(false)
    }
    window.addEventListener('mousedown', handler)
    return () => window.removeEventListener('mousedown', handler)
  }, [open])

  function toggle() {
    if (open) { setOpen(false); return }
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) { setOpen(true); return }
    const flipUp = window.innerHeight - rect.bottom < CALENDAR_H + 8
    const left = Math.min(rect.left, window.innerWidth - CALENDAR_W - 8)
    if (flipUp) {
      setPos({ top: 'auto', left, bottom: window.innerHeight - rect.top + 4, flipUp: true })
    } else {
      setPos({ top: rect.bottom + 4, left, bottom: 'auto', flipUp: false })
    }
    setOpen(true)
  }

  const daysInMonth = getDaysInMonth(viewYear, viewMonth)
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth)

  function selectDay(day) {
    const d = new Date(viewYear, viewMonth, day)
    d.setHours(12, 0, 0, 0)
    onChange(d.toISOString())
    setOpen(false)
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1) }
    else setViewMonth(m => m - 1)
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1) }
    else setViewMonth(m => m + 1)
  }

  const selectedDate = parseLocalDate(value)

  return (
    <div className="relative inline-block">
      {children ? (
        <div ref={triggerRef} onClick={toggle} className="cursor-pointer">
          {children}
        </div>
      ) : (
        <button
          ref={triggerRef}
          onClick={toggle}
          className={cn(
            'flex items-center gap-1.5 text-sm transition-colors',
            value ? 'text-text' : 'text-text-faint hover:text-text-muted'
          )}
        >
          <Calendar size={13} />
          {value ? formatDate(value) : placeholder}
          {value && (
            <button
              type="button"
              aria-label="Clear date"
              onClick={(e) => { e.stopPropagation(); onChange(null) }}
              className="ml-1 text-text-faint hover:text-text-muted"
            >
              <X size={11} aria-hidden="true" />
            </button>
          )}
        </button>
      )}

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={calendarRef}
              data-floating-ui
              initial={{ opacity: 0, scale: 0.95, y: pos.flipUp ? 4 : -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: pos.flipUp ? 4 : -4 }}
              transition={{ duration: 0.14, ease: [0.23, 1, 0.32, 1] }}
              style={{
                position: 'fixed',
                top: pos.top,
                bottom: pos.bottom,
                left: pos.left,
                width: CALENDAR_W,
                transformOrigin: pos.flipUp ? 'bottom left' : 'top left',
              }}
              className="z-[9000] bg-surface-raised border border-border rounded-xl shadow-xl shadow-black/30 p-3"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <button onClick={prevMonth} aria-label="Previous month" className="p-1 rounded hover:bg-surface text-text-muted hover:text-text transition-colors active:scale-[0.97]">
                  <ChevronLeft size={14} aria-hidden="true" />
                </button>
                <span className="text-sm font-medium text-text">{MONTHS[viewMonth]} {viewYear}</span>
                <button onClick={nextMonth} aria-label="Next month" className="p-1 rounded hover:bg-surface text-text-muted hover:text-text transition-colors active:scale-[0.97]">
                  <ChevronRight size={14} aria-hidden="true" />
                </button>
              </div>

              {/* Day names */}
              <div className="grid grid-cols-7 mb-1">
                {DAYS.map(d => (
                  <div key={d} className="text-center text-[10px] text-text-faint font-medium py-1">{d}</div>
                ))}
              </div>

              {/* Days grid */}
              <div className="grid grid-cols-7 gap-0.5">
                {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1
                  const isToday = today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === day
                  const isSelected = selectedDate && selectedDate.getFullYear() === viewYear && selectedDate.getMonth() === viewMonth && selectedDate.getDate() === day
                  return (
                    <button
                      key={day}
                      onClick={() => selectDay(day)}
                      className={cn(
                        'aspect-square flex items-center justify-center text-xs rounded-lg transition-all active:scale-[0.94]',
                        isSelected
                          ? 'bg-accent text-white font-semibold'
                          : isToday
                            ? 'ring-1 ring-accent text-accent font-medium'
                            : 'text-text-muted hover:bg-surface hover:text-text'
                      )}
                    >
                      {day}
                    </button>
                  )
                })}
              </div>

              {/* Quick picks */}
              <div className="flex gap-1 mt-2 pt-2 border-t border-border">
                {[['Today', 0], ['Tomorrow', 1], ['Next week', 7]].map(([label, days]) => (
                  <button
                    key={label}
                    onClick={() => {
                      const d = new Date()
                      d.setDate(d.getDate() + days)
                      d.setHours(12, 0, 0, 0)
                      onChange(d.toISOString())
                      setOpen(false)
                    }}
                    className="flex-1 text-[10px] py-1 rounded-lg bg-surface text-text-muted hover:text-text hover:bg-surface/80 transition-colors active:scale-[0.97]"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  )
}
